import { useEffect, useState } from "react"

import {
  acceptDealAskRobSuggestion,
  generateDealAskRobReview,
  getDealAskRob,
  rejectDealAskRobSuggestion,
  sendDealAskRobMessage,
} from "@/lib/api"
import { looksPersistedDealId } from "@/lib/persisted-deal"
import { useSession } from "@/lib/session-context"

export function useDealAskRob({ deal, tab = "overview", onSuggestionAccepted } = {}) {
  const { currentUser, csrfToken } = useSession()
  const canUsePersistedDealApi = Boolean(currentUser && deal?.id && looksPersistedDealId(deal.id))
  const [thread, setThread] = useState(null)
  const [review, setReview] = useState({ status: "not_generated", sections: null })
  const [input, setInput] = useState("")
  const [isLoading, setIsLoading] = useState(canUsePersistedDealApi)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isReviewing, setIsReviewing] = useState(false)
  const [busySuggestionId, setBusySuggestionId] = useState(null)
  const [error, setError] = useState(null)
  const [reviewError, setReviewError] = useState(null)
  const messages = thread?.messages ?? []
  const pendingSuggestions = (thread?.suggestions ?? []).filter((suggestion) => suggestion.status === "pending")

  useEffect(() => {
    if (!canUsePersistedDealApi) return undefined

    let ignore = false

    getDealAskRob(deal.id, { csrfToken })
      .then((payload) => {
        if (ignore) return

        setThread(payload.thread ?? null)
        setReview(payload.review ?? { status: "not_generated", sections: null })
        setError(null)
      })
      .catch((requestError) => {
        if (!ignore) setError(requestError.message)
      })
      .finally(() => {
        if (!ignore) setIsLoading(false)
      })

    return () => { ignore = true }
  }, [csrfToken, deal?.id, canUsePersistedDealApi])

  async function submitMessage(message) {
    const body = message.trim()
    if (!body || isSubmitting || !canUsePersistedDealApi) return

    setIsSubmitting(true)
    setError(null)

    try {
      const payload = await sendDealAskRobMessage(deal.id, {
        body,
        metadata: { tab },
      }, { csrfToken })
      setThread(payload.thread ?? null)
      setInput("")
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  async function generateReview() {
    if (isReviewing || !canUsePersistedDealApi) return

    setIsReviewing(true)
    setReviewError(null)

    try {
      const payload = await generateDealAskRobReview(deal.id, { csrfToken })
      setReview(payload.review ?? { status: "not_generated", sections: null })
      setThread(payload.thread ?? null)
    } catch (requestError) {
      setReviewError(requestError.message)
    } finally {
      setIsReviewing(false)
    }
  }

  async function acceptSuggestion(suggestionId) {
    if (!canUsePersistedDealApi || busySuggestionId) return

    setBusySuggestionId(suggestionId)
    setError(null)

    try {
      const payload = await acceptDealAskRobSuggestion(deal.id, suggestionId, { csrfToken })
      setThread(payload.thread ?? null)
      onSuggestionAccepted?.(payload)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setBusySuggestionId(null)
    }
  }

  async function rejectSuggestion(suggestionId) {
    if (!canUsePersistedDealApi || busySuggestionId) return

    setBusySuggestionId(suggestionId)
    setError(null)

    try {
      const payload = await rejectDealAskRobSuggestion(
        deal.id,
        suggestionId,
        { reason: "Dismissed from deal card" },
        { csrfToken },
      )
      setThread(payload.thread ?? null)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setBusySuggestionId(null)
    }
  }

  return {
    input,
    setInput,
    messages,
    review,
    pendingSuggestions,
    isLoading,
    isSubmitting,
    isReviewing,
    busySuggestionId,
    error,
    reviewError,
    canUsePersistedDealApi,
    submitMessage,
    generateReview,
    acceptSuggestion,
    rejectSuggestion,
  }
}
